import {
  makeStyles,
  tokens,
  Title2,
  Body1,
  Button,
  Card,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from '@fluentui/react-components'
import { 
  Video24Regular, 
  People24Regular, 
  Chat24Regular,
  SignOut24Regular,
  Settings24Regular,
  HeartPulse24Regular,
} from '@fluentui/react-icons'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'

const useStyles = makeStyles({
  container: {
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: tokens.spacingVerticalL,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  userInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
  },
  content: {
    padding: tokens.spacingVerticalXXL,
    maxWidth: '1200px',
    margin: '0 auto',
  },
  welcome: {
    marginBottom: tokens.spacingVerticalXL,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: tokens.spacingVerticalL,
  },
  card: {
    padding: tokens.spacingVerticalL,
    cursor: 'pointer',
    transition: 'box-shadow 0.2s',
    ':hover': {
      boxShadow: tokens.shadow8,
    },
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalM,
  },
  cardIcon: {
    fontSize: '32px',
    color: tokens.colorBrandForeground1,
  },
})

export function Dashboard() {
  const styles = useStyles()
  const { userName, logout } = useAuthStore()
  const navigate = useNavigate()

  const features = [
    {
      icon: <Video24Regular className={styles.cardIcon} />,
      title: 'Cases',
      description: 'Create and manage investigation cases',
      onClick: () => navigate('/cases'),
    },
    {
      icon: <People24Regular className={styles.cardIcon} />,
      title: 'People',
      description: 'View identified individuals across videos',
    },
    {
      icon: <Chat24Regular className={styles.cardIcon} />,
      title: 'Chat',
      description: 'Query your evidence with natural language',
    },
  ]

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div className={styles.headerTitle}>
          <Title2>The Librarian</Title2>
        </div>
        <div className={styles.userInfo}>
          <Body1>{userName}</Body1>
          <Menu>
            <MenuTrigger disableButtonEnhancement>
              <Button
                icon={<Settings24Regular />}
                appearance="subtle"
                aria-label="Settings"
              />
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                <MenuItem
                  icon={<HeartPulse24Regular />}
                  onClick={() => navigate('/health')}
                >
                  Solution Health
                </MenuItem>
              </MenuList>
            </MenuPopover>
          </Menu>
          <Button 
            icon={<SignOut24Regular />} 
            appearance="subtle"
            onClick={logout}
          >
            Sign Out
          </Button>
        </div>
      </header>
      
      <main className={styles.content}>
        <div className={styles.welcome}>
          <Title2>Welcome back, {userName}</Title2>
          <Body1>Select a case or browse your evidence library.</Body1>
        </div>
        
        <div className={styles.grid}>
          {features.map((feature) => (
            <Card
              key={feature.title}
              className={styles.card}
              onClick={feature.onClick}
            >
              <div className={styles.cardHeader}>
                {feature.icon}
                <Title2>{feature.title}</Title2>
              </div>
              <Body1>{feature.description}</Body1>
            </Card>
          ))}
        </div>
      </main>
    </div>
  )
}
